import { useState, useCallback } from "react";
import { useDropzone } from "react-dropzone";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/lib/supabase";
import {
  FileText, Image as ImageIcon, FileSpreadsheet, File, Presentation,
  Download, Eye, Trash2, Upload, Loader2, Paperclip, X, Check
} from "lucide-react";
import type { ActionAttachment } from "@shared/schema";

interface Props {
  actionId: string;
  readOnly?: boolean;
}

const MAX_FILES = 5;
const MAX_SIZE_BYTES = 25 * 1024 * 1024; // 25 MB

const ALLOWED_EXTENSIONS = [
  ".pdf", ".jpg", ".jpeg", ".png", ".webp", ".gif", ".heic", ".heif",
  ".doc", ".docx", ".xls", ".xlsx", ".ppt", ".pptx",
  ".odt", ".ods", ".odp", ".txt", ".csv", ".zip",
];

function getMimeIcon(mimeType: string) {
  if (mimeType === "application/pdf") return <FileText className="w-4 h-4 text-red-500 shrink-0" />;
  if (mimeType.startsWith("image/")) return <ImageIcon className="w-4 h-4 text-blue-400 shrink-0" />;
  if (mimeType.includes("spreadsheet") || mimeType.includes("excel") || mimeType === "text/csv")
    return <FileSpreadsheet className="w-4 h-4 text-green-500 shrink-0" />;
  if (mimeType.includes("presentation") || mimeType.includes("powerpoint"))
    return <Presentation className="w-4 h-4 text-orange-500 shrink-0" />;
  if (mimeType.includes("word") || mimeType.includes("opendocument.text"))
    return <FileText className="w-4 h-4 text-blue-600 shrink-0" />;
  return <File className="w-4 h-4 text-muted-foreground shrink-0" />;
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} Ko`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
}

function canPreview(mimeType: string): boolean {
  return mimeType === "application/pdf" || mimeType.startsWith("image/");
}

interface UploadingFile {
  file: File;
  progress: number;
  error?: string;
  done?: boolean;
}

export default function ActionAttachmentsZone({ actionId, readOnly = false }: Props) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [uploading, setUploading] = useState<UploadingFile[]>([]);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  const { data: attachments = [], isLoading } = useQuery<ActionAttachment[]>({
    queryKey: ["/api/tasks", actionId, "attachments"],
    queryFn: async () => {
      const res = await fetch(`/api/tasks/${actionId}/attachments`, {
        headers: { Authorization: `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}` },
      });
      if (!res.ok) throw new Error("Impossible de charger les pièces jointes");
      return res.json();
    },
    enabled: !!actionId,
  });

  const uploadFile = useCallback(async (file: File) => {
    const entry: UploadingFile = { file, progress: 0 };
    setUploading(prev => [...prev, entry]);

    try {
      const session = (await supabase.auth.getSession()).data.session;
      if (!session) throw new Error("Non authentifié");

      const formData = new FormData();
      formData.append("file", file);

      await new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) {
            const pct = Math.round((e.loaded / e.total) * 100);
            setUploading(prev => prev.map(u => u.file === file ? { ...u, progress: pct } : u));
          }
        };
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            setUploading(prev => prev.map(u => u.file === file ? { ...u, progress: 100, done: true } : u));
            resolve();
          } else {
            let msg = "Erreur d'upload";
            try { msg = JSON.parse(xhr.responseText)?.message || msg; } catch {}
            if (xhr.status === 413) msg = "Fichier trop volumineux (max 25 Mo)";
            if (xhr.status === 415) msg = "Type de fichier non autorisé";
            if (xhr.status === 409) msg = "Limite de 5 pièces jointes atteinte";
            setUploading(prev => prev.map(u => u.file === file ? { ...u, error: msg } : u));
            reject(new Error(msg));
          }
        };
        xhr.onerror = () => {
          const msg = "Erreur réseau";
          setUploading(prev => prev.map(u => u.file === file ? { ...u, error: msg } : u));
          reject(new Error(msg));
        };
        xhr.open("POST", `/api/tasks/${actionId}/attachments`);
        xhr.setRequestHeader("Authorization", `Bearer ${session.access_token}`);
        xhr.send(formData);
      });

      await queryClient.invalidateQueries({ queryKey: ["/api/tasks", actionId, "attachments"] });
      setTimeout(() => setUploading(prev => prev.filter(u => u.file !== file)), 1500);
    } catch (err: any) {
      toast({ variant: "destructive", title: "Upload échoué", description: err.message });
      setTimeout(() => setUploading(prev => prev.filter(u => u.file !== file)), 3000);
    }
  }, [actionId, queryClient, toast]);

  const onDrop = useCallback((accepted: File[], rejected: any[]) => {
    const available = MAX_FILES - attachments.length - uploading.filter(u => !u.error && !u.done).length;
    rejected.forEach(({ file, errors }) => {
      const msg = errors[0]?.code === "file-too-large"
        ? `${file.name} : trop volumineux (max 25 Mo)`
        : errors[0]?.code === "file-invalid-type"
        ? `${file.name} : type de fichier non autorisé`
        : `${file.name} : rejeté`;
      toast({ variant: "destructive", title: "Fichier refusé", description: msg });
    });
    accepted.slice(0, available).forEach(uploadFile);
    if (accepted.length > available) {
      toast({ variant: "destructive", title: "Limite atteinte", description: `Maximum ${MAX_FILES} pièces jointes par action.` });
    }
  }, [attachments.length, uploading, uploadFile, toast]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    maxSize: MAX_SIZE_BYTES,
    accept: {
      "application/pdf": [".pdf"],
      "image/*": [".jpg", ".jpeg", ".png", ".webp", ".gif", ".heic", ".heif"],
      "application/msword": [".doc"],
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx"],
      "application/vnd.ms-excel": [".xls"],
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
      "application/vnd.ms-powerpoint": [".ppt"],
      "application/vnd.openxmlformats-officedocument.presentationml.presentation": [".pptx"],
      "application/vnd.oasis.opendocument.text": [".odt"],
      "application/vnd.oasis.opendocument.spreadsheet": [".ods"],
      "application/vnd.oasis.opendocument.presentation": [".odp"],
      "text/plain": [".txt"],
      "text/csv": [".csv"],
      "application/zip": [".zip"],
    },
    disabled: readOnly || (attachments.length + uploading.filter(u => !u.error).length >= MAX_FILES),
    multiple: true,
  });

  const handleDownload = async (att: ActionAttachment) => {
    try {
      const session = (await supabase.auth.getSession()).data.session;
      const res = await fetch(`/api/tasks/${actionId}/attachments/${att.id}/download`, {
        headers: { Authorization: `Bearer ${session?.access_token}` },
      });
      if (!res.ok) throw new Error("Impossible de télécharger");
      const { url } = await res.json();
      window.open(url, "_blank", "noopener");
    } catch (err: any) {
      toast({ variant: "destructive", title: "Erreur", description: err.message });
    }
  };

  const handleDelete = async (att: ActionAttachment) => {
    setDeletingId(att.id);
    setConfirmDeleteId(null);
    try {
      const session = (await supabase.auth.getSession()).data.session;
      const res = await fetch(`/api/tasks/${actionId}/attachments/${att.id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${session?.access_token}` },
      });
      if (!res.ok && res.status !== 404) throw new Error("Suppression impossible");
      await queryClient.invalidateQueries({ queryKey: ["/api/tasks", actionId, "attachments"] });
    } catch (err: any) {
      toast({ variant: "destructive", title: "Erreur", description: err.message });
    } finally {
      setDeletingId(null);
    }
  };

  const totalCount = attachments.length + uploading.filter(u => !u.error && !u.done).length;
  const atLimit = totalCount >= MAX_FILES;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Paperclip className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm font-medium">Pièces jointes</span>
        </div>
        <Badge variant={atLimit ? "destructive" : "secondary"} className="text-xs tabular-nums">
          {attachments.length} / {MAX_FILES}
        </Badge>
      </div>

      {isLoading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
          <Loader2 className="w-3.5 h-3.5 animate-spin" /> Chargement…
        </div>
      ) : (
        <>
          {/* Liste des PJ existantes */}
          {(attachments.length > 0 || uploading.length > 0) && (
            <div className="space-y-1">
              {attachments.map(att => (
                <div
                  key={att.id}
                  className="flex items-center gap-2 p-2 rounded-md border bg-muted/30 hover:bg-muted/50 transition-colors text-sm"
                  data-testid={`attachment-item-${att.id}`}
                >
                  {getMimeIcon(att.mimeType)}
                  <span className="flex-1 min-w-0 truncate" title={att.originalFilename}>
                    {att.originalFilename}
                  </span>
                  <span className="text-xs text-muted-foreground shrink-0">{formatSize(att.sizeBytes)}</span>

                  {/* Aperçu */}
                  {canPreview(att.mimeType) && (
                    <Button
                      variant="ghost" size="icon" className="h-6 w-6 shrink-0"
                      onClick={() => handleDownload(att)}
                      title="Aperçu dans un nouvel onglet"
                      type="button"
                      data-testid={`btn-preview-${att.id}`}
                    >
                      <Eye className="h-3.5 w-3.5" />
                    </Button>
                  )}

                  {/* Téléchargement */}
                  <Button
                    variant="ghost" size="icon" className="h-6 w-6 shrink-0"
                    onClick={() => handleDownload(att)}
                    title="Télécharger"
                    type="button"
                    data-testid={`btn-download-${att.id}`}
                  >
                    <Download className="h-3.5 w-3.5" />
                  </Button>

                  {/* Suppression */}
                  {!readOnly && (
                    confirmDeleteId === att.id ? (
                      <div className="flex items-center gap-1 shrink-0">
                        <Button
                          variant="destructive" size="icon" className="h-6 w-6"
                          onClick={() => handleDelete(att)}
                          disabled={deletingId === att.id}
                          type="button"
                          data-testid={`btn-confirm-delete-${att.id}`}
                        >
                          {deletingId === att.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
                        </Button>
                        <Button
                          variant="ghost" size="icon" className="h-6 w-6"
                          onClick={() => setConfirmDeleteId(null)}
                          type="button"
                          data-testid={`btn-cancel-delete-${att.id}`}
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                    ) : (
                      <Button
                        variant="ghost" size="icon" className="h-6 w-6 shrink-0 text-muted-foreground hover:text-destructive"
                        onClick={() => setConfirmDeleteId(att.id)}
                        type="button"
                        data-testid={`btn-delete-${att.id}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    )
                  )}
                </div>
              ))}

              {/* Fichiers en cours d'upload */}
              {uploading.map((u, i) => (
                <div key={i} className="flex flex-col gap-1 p-2 rounded-md border bg-blue-50/50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-800/40 text-sm">
                  <div className="flex items-center gap-2">
                    {u.error ? (
                      <File className="w-4 h-4 text-destructive shrink-0" />
                    ) : u.done ? (
                      <Check className="w-4 h-4 text-green-500 shrink-0" />
                    ) : (
                      <Loader2 className="w-4 h-4 text-blue-500 animate-spin shrink-0" />
                    )}
                    <span className="flex-1 min-w-0 truncate" title={u.file.name}>{u.file.name}</span>
                    <span className="text-xs text-muted-foreground shrink-0">{formatSize(u.file.size)}</span>
                  </div>
                  {!u.error && !u.done && (
                    <Progress value={u.progress} className="h-1" />
                  )}
                  {u.error && (
                    <p className="text-xs text-destructive">{u.error}</p>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Dropzone */}
          {!readOnly && (
            <div
              {...getRootProps()}
              className={`relative border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors ${
                atLimit
                  ? "border-muted bg-muted/20 cursor-not-allowed opacity-50"
                  : isDragActive
                  ? "border-primary bg-primary/5"
                  : "border-muted-foreground/25 hover:border-primary/50 hover:bg-muted/20"
              }`}
              data-testid="dropzone-attachments"
            >
              <input {...getInputProps()} data-testid="input-attachments-file" />
              <div className="flex flex-col items-center gap-1.5">
                <Upload className={`h-5 w-5 ${isDragActive ? "text-primary" : "text-muted-foreground"}`} />
                {atLimit ? (
                  <p className="text-xs text-muted-foreground">Limite de {MAX_FILES} pièces jointes atteinte</p>
                ) : (
                  <>
                    <p className="text-sm text-muted-foreground">
                      {isDragActive ? "Déposez ici…" : "Glissez-déposez ou "}
                      {!isDragActive && <span className="text-primary font-medium">parcourez</span>}
                    </p>
                    <p className="text-xs text-muted-foreground">PDF, images, Office, CSV, ZIP · max 25 Mo</p>
                  </>
                )}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
